import React from 'react';
import PropTypes from 'prop-types';
import { typography, textColors } from '../../tokens';

/**
 * FormField Component
 * 
 * Wrapper component that handles the layout of label, input, error, and helper text.
 * Provides consistent spacing and accessibility for all form fields.
 * 
 * @example
 * // Basic form field
 * <FormField label="Email" htmlFor="email">
 *   <Input
 *     id="email"
 *     type="email"
 *     value={email}
 *     onChange={(e) => setEmail(e.target.value)}
 *   />
 * </FormField>
 * 
 * @example
 * // Form field with helper text
 * <FormField 
 *   label="Password" 
 *   htmlFor="password"
 *   helperText="Must be at least 8 characters"
 *   required
 * >
 *   <Input
 *     id="password"
 *     type="password"
 *     value={password}
 *     onChange={(e) => setPassword(e.target.value)}
 *   />
 * </FormField>
 */
const FormField = ({
  // Label
  label = '',
  htmlFor,
  required = false,
  optional = false,
  error = '',
  
  // Helper text (displayed below field, separate from input helperText)
  helperText = '',
  
  // Layout
  layout = 'vertical', // 'vertical' | 'horizontal'
  
  // Children (the input component)
  children,
  
  // Styling
  className = '',
  labelClassName = '',
  
  // Additional props
  ...rest
}) => {
  // Container classes
  const containerClasses = [
    layout === 'horizontal' ? 'flex items-start space-x-4' : 'flex flex-col',
    className,
  ].filter(Boolean).join(' ');
  
  // Label wrapper classes
  const labelWrapperClasses = layout === 'horizontal' ? 'flex-shrink-0 pt-2' : 'mb-2';
  
  // Label classes
  const baseLabelClasses = `block ${typography.label} ${textColors.secondary}`;
  const combinedLabelClasses = [baseLabelClasses, labelClassName].filter(Boolean).join(' ');
  
  // Input wrapper classes
  const inputWrapperClasses = layout === 'horizontal' ? 'flex-1' : '';
  
  return (
    <div className={containerClasses} {...rest}>
      {/* Label */}
      {label && (
        <div className={labelWrapperClasses}>
          <label htmlFor={htmlFor} className={combinedLabelClasses}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
            {optional && !required && <span className={`ml-1 font-normal ${typography.caption} ${textColors.muted}`}>(Optional)</span>}
          </label>
        </div>
      )}
      
      {/* Input */}
      <div className={inputWrapperClasses}>
        {children}
        
        {/* Error Text */}
        {!!error && (
          <p className={`mt-1 ${typography.caption} text-red-600`}>
            {error}
          </p>
        )}

        {/* Helper Text (if not already shown by the input component) */}
        {helperText && (
          <p className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>
            {helperText}
          </p>
        )}
      </div>
    </div>
  );
};

FormField.propTypes = {
  label: PropTypes.string,
  htmlFor: PropTypes.string,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  
  helperText: PropTypes.string,
  
  layout: PropTypes.oneOf(['vertical', 'horizontal']),
  
  children: PropTypes.node.isRequired,
  
  className: PropTypes.string,
  labelClassName: PropTypes.string,
};

export default FormField;
